// BCH School curriculum data.
// ALL numbers, balances, fees, and transactions in this file are SIMULATED
// for educational purposes only. No real money or real transactions occur.

export type ModuleId =
  | "basics"
  | "inflation"
  | "wallets"
  | "send-receive"
  | "payments"
  | "merchant"
  | "bch-vs-btc"
  | "safety"
  | "cashtokens"
  | "real-world";

export interface Choice {
  id: string;
  /** The button label the learner clicks. */
  label: string;
  /** Whether this is the "best" choice (grants full XP). */
  correct: boolean;
  /** What happens when this choice is made — the "See the result" step. */
  result: string;
  /** Tone of the result, drives styling. */
  outcome: "good" | "bad" | "neutral";
}

export interface Lesson {
  id: string;
  moduleId: ModuleId;
  title: string;
  /** Short one-liner shown in lists. */
  summary: string;
  /** XP earned for completing this lesson. */
  xp: number;
  /** The "Learn" step — plain-language teaching content (paragraphs). */
  learn: string[];
  /** A fun, illustrative simulated stat or fact box (optional). */
  factBox?: { label: string; value: string }[];
  /** The scenario prompt for the "Make a decision" step. */
  scenario: string;
  choices: Choice[];
  /** The "Understand why" explanation shown after any choice. */
  why: string;
}

export interface Module {
  id: ModuleId;
  title: string;
  tagline: string;
  /** Emoji icon for quick visual identity. */
  emoji: string;
  /** Accent color token for cards (tailwind gradient stops). */
  gradient: string;
  /** Illustration shown on the module card and inside its lessons. */
  image: string;
  lessons: Lesson[];
}

export const MODULES: Module[] = [
  {
    id: "basics",
    title: "BCH Basics",
    tagline: "What Bitcoin Cash is and why it exists.",
    emoji: "🌱",
    gradient: "from-green-500 to-emerald-600",
    image: "/modules/basics.jpg",
    lessons: [
      {
        id: "basics-1",
        moduleId: "basics",
        title: "What is Bitcoin Cash?",
        summary: "Digital cash for the world.",
        xp: 20,
        learn: [
          "Bitcoin Cash (BCH) is peer-to-peer electronic cash. That means you can send money directly to another person over the internet — no bank, no company, and no middleman standing in between.",
          "It works a bit like handing someone physical cash, except it happens over the internet and can reach anyone on Earth in seconds.",
          "BCH was created in 2017 so that everyday payments could stay fast and cheap, keeping the original promise of Bitcoin as usable money — not just something you hold and hope goes up.",
        ],
        factBox: [
          { label: "Born", value: "August 2017" },
          { label: "Typical fee", value: "less than 1 cent (simulated)" },
          { label: "Confirmation", value: "seconds to a few minutes" },
        ],
        scenario:
          "Your friend across the country needs $10 right now. It's 11pm on a Sunday. How would BCH help here?",
        choices: [
          {
            id: "a",
            label: "Send BCH directly — it arrives in seconds, any time of day",
            correct: true,
            outcome: "good",
            result:
              "✅ Done! The $10 in BCH lands in your friend's wallet almost instantly. Banks are closed, but the BCH network never sleeps.",
          },
          {
            id: "b",
            label: "Wait until the bank opens on Monday",
            correct: false,
            outcome: "bad",
            result:
              "⏳ Your friend waits ~36 hours. This is exactly the kind of delay BCH was designed to remove.",
          },
        ],
        why: "BCH is money that lives on an open network running 24/7. Because there's no bank in the middle, payments don't wait for business hours, borders, or holidays.",
      },
      {
        id: "basics-2",
        moduleId: "basics",
        title: "Why does BCH exist?",
        summary: "Keeping payments fast and cheap.",
        xp: 20,
        learn: [
          "Early Bitcoin promised cheap, fast payments for everyone. As it grew popular, it got congested — fees sometimes rose to several dollars and payments could be slow.",
          "A group of people believed money should stay usable for buying coffee, sending remittances, and tipping — not just for large investments. So Bitcoin Cash was created to keep fees low and blocks roomy enough for lots of everyday transactions.",
          "The core idea: money you actually spend, not just store.",
        ],
        factBox: [
          { label: "Design goal", value: "Everyday spending" },
          { label: "Block space", value: "Large (room for many payments)" },
          { label: "Fees", value: "Kept tiny on purpose" },
        ],
        scenario:
          "You want to tip a street musician $2. Which network fits this best?",
        choices: [
          {
            id: "a",
            label: "A network where the fee is a fraction of a cent",
            correct: true,
            outcome: "good",
            result:
              "✅ The musician gets nearly the full $2. A tiny tip like this makes sense when fees are basically nothing.",
          },
          {
            id: "b",
            label: "A network where the fee could be $3",
            correct: false,
            outcome: "bad",
            result:
              "❌ The fee costs more than the tip itself! Small everyday payments stop making sense.",
          },
        ],
        why: "Low fees aren't just a nice-to-have — they decide what money can be used for. When fees are tiny, micro-payments, tips, and small purchases all become practical. That's the whole point of BCH.",
      },
    ],
  },
  {
    id: "inflation",
    title: "The Inflation Problem",
    tagline: "Why your money loses value over time.",
    emoji: "📉",
    gradient: "from-amber-500 to-orange-600",
    image: "/modules/inflation.jpg",
    lessons: [
      {
        id: "inflation-1",
        moduleId: "inflation",
        title: "What is inflation?",
        summary: "When money buys less each year.",
        xp: 20,
        learn: [
          "Inflation means prices rise over time — so the same amount of money buys less than it used to. Your dollars don't change, but what they can get you slowly shrinks.",
          "You've probably felt it: a candy bar, a coffee, or a movie ticket that cost far less when you were a kid. The item didn't get more valuable — your money got weaker.",
          "A little inflation is normal in most economies. The problem is what it quietly does to your savings: money sitting still loses purchasing power every single year, even if the number in your account never drops.",
        ],
        factBox: [
          { label: "$100 in 1990", value: "~$240 to match today (simulated)" },
          { label: "Coffee, 2000", value: "~$1.50 (simulated)" },
          { label: "Coffee, today", value: "~$4.50 (simulated)" },
        ],
        scenario:
          "You keep $1,000 in cash under your mattress. Prices rise about 5% this year (simulated). At year's end, what happened to your money?",
        choices: [
          {
            id: "a",
            label: "It still says $1,000, but it buys about $50 less",
            correct: true,
            outcome: "good",
            result:
              "✅ Exactly. The number didn't change, but its purchasing power quietly fell. That hidden loss is inflation at work.",
          },
          {
            id: "b",
            label: "Nothing changed — it's still $1,000",
            correct: false,
            outcome: "bad",
            result:
              "❌ The number is the same, but each dollar now buys less. Standing still means falling behind when prices rise.",
          },
        ],
        why: "Inflation is a silent tax on savers. Because it works through prices rather than your balance, it's easy to miss — but over years it can erode a huge chunk of what your money is worth.",
      },
      {
        id: "inflation-2",
        moduleId: "inflation",
        title: "Where does inflation come from?",
        summary: "Printing more money.",
        xp: 20,
        learn: [
          "One of the biggest drivers of inflation is the money supply growing. When far more money is created and chases the same amount of goods, each unit of money becomes worth a little less.",
          "Governments and central banks can create new money — for example to fund spending or respond to a crisis. It can help in the short term, but if too much is made, prices climb.",
          "Think of it like concert tickets: if the venue suddenly prints twice as many tickets for the same show, each ticket is worth less. Money works the same way — scarcity is part of what gives it value.",
        ],
        factBox: [
          { label: "More money, same goods", value: "Prices rise" },
          { label: "Key property of money", value: "Scarcity" },
          { label: "Who can print more?", value: "Central banks" },
        ],
        scenario:
          "Imagine a small island uses seashells as money. One day a machine is invented that makes shells instantly and endlessly. What happens to the shells' value?",
        choices: [
          {
            id: "a",
            label: "They become nearly worthless — anyone can make unlimited shells",
            correct: true,
            outcome: "good",
            result:
              "✅ Right. Once something can be created without limit, it stops being good money. Scarcity is what protects value.",
          },
          {
            id: "b",
            label: "They get more valuable because there are more of them",
            correct: false,
            outcome: "bad",
            result:
              "❌ More supply of the same thing means each one is worth less, not more. Abundance destroys money's value.",
          },
        ],
        why: "Money holds its value partly because it's scarce and hard to create. When the supply can be expanded at will, holders quietly pay the price through inflation. This is the core weakness of money that anyone can print.",
      },
      {
        id: "inflation-3",
        moduleId: "inflation",
        title: "How sound money fights back",
        summary: "Scarcity you can count on.",
        xp: 25,
        learn: [
          "'Sound money' is money that can't be casually printed into oblivion — its supply is limited and predictable. Historically that was gold; today, Bitcoin and Bitcoin Cash bring that idea to the digital world.",
          "Bitcoin Cash has a supply capped at 21 million coins, released on a fixed, transparent schedule that no government or company can change. Nobody can suddenly create a billion more BCH to dilute what you hold.",
          "That doesn't make BCH's day-to-day price stable — it still moves. But over the long run, a hard cap means your slice of the pie can't be quietly inflated away. Combine that with BCH's low fees, and you get money that's both spendable AND resistant to debasement.",
        ],
        factBox: [
          { label: "BCH max supply", value: "21 million (fixed)" },
          { label: "Who can change it?", value: "No one" },
          { label: "Inflation over time", value: "Trends toward zero" },
        ],
        scenario:
          "You want to store value for the long term in something that can't be printed at will. Which property matters most?",
        choices: [
          {
            id: "a",
            label: "A fixed, transparent supply no one can inflate",
            correct: true,
            outcome: "good",
            result:
              "✅ That's the heart of sound money. A supply nobody can expand protects your purchasing power from being diluted.",
          },
          {
            id: "b",
            label: "A supply that authorities can expand whenever they choose",
            correct: false,
            outcome: "bad",
            result:
              "⚠️ That's exactly what lets value erode over time. Discretionary printing is the opposite of sound money.",
          },
        ],
        why: "BCH was built as peer-to-peer cash with a hard supply cap — spendable like cash, but scarce like gold. Understanding inflation is the whole reason this matters: sound money is the problem BCH exists to solve. Everything else in this course builds on that foundation.",
      },
    ],
  },
  {
    id: "wallets",
    title: "Wallets & Self-Custody",
    tagline: "Be your own bank — safely.",
    emoji: "🔐",
    gradient: "from-teal-500 to-cyan-600",
    image: "/modules/wallets.jpg",
    lessons: [
      {
        id: "wallets-1",
        moduleId: "wallets",
        title: "What is a wallet?",
        summary: "Keys, not coins.",
        xp: 20,
        learn: [
          "A BCH wallet is an app that holds your keys — not your coins. Your coins live on the network; the wallet holds the secret key that proves the coins are yours.",
          "Your public address is like an email address you share so people can send you BCH. Your private key (and the recovery phrase that backs it up) is like the password — never share it.",
          "'Self-custody' means you hold your own keys. You are in full control, and no company can freeze or lose your funds for you.",
        ],
        factBox: [
          { label: "Address", value: "Share freely (like email)" },
          { label: "Recovery phrase", value: "12 secret words — never share" },
          { label: "Custody", value: "You hold the keys" },
        ],
        scenario:
          "A stranger online offers to 'help set up your wallet' and asks you to read them your 12-word recovery phrase. What do you do?",
        choices: [
          {
            id: "a",
            label: "Never share it — politely decline",
            correct: true,
            outcome: "good",
            result:
              "✅ Smart. Your recovery phrase is the master key to all your funds. Nobody legitimate will ever need it.",
          },
          {
            id: "b",
            label: "Share it — they seem helpful",
            correct: false,
            outcome: "bad",
            result:
              "❌ (Simulated) The 'helper' drains the wallet instantly. Anyone with your 12 words owns your money — permanently.",
          },
        ],
        why: "Your recovery phrase IS your money. Whoever holds those words controls the funds. Real support, real apps, and real friends will never ask for it. Guard it like the keys to a vault.",
      },
      {
        id: "wallets-2",
        moduleId: "wallets",
        title: "Backing up your keys",
        summary: "Write it down, keep it safe.",
        xp: 20,
        learn: [
          "When you create a self-custody wallet, it shows you a recovery phrase — usually 12 words. This phrase can restore your money on any device if your phone is lost, broken, or stolen.",
          "Because YOU hold the keys, there's no 'forgot password' button and no support line to recover it. The backup is entirely your responsibility.",
          "Best practice: write it on paper (or metal), store it somewhere safe and private, and never store it as a plain screenshot in the cloud.",
        ],
        scenario:
          "You just made a wallet and it shows your 12-word phrase. Where do you store it?",
        choices: [
          {
            id: "a",
            label: "Write it on paper and store it somewhere private and safe",
            correct: true,
            outcome: "good",
            result:
              "✅ If your phone ever dies, you type the 12 words into a new wallet and your funds reappear. Peace of mind.",
          },
          {
            id: "b",
            label: "Screenshot it and save it to your photos / cloud",
            correct: false,
            outcome: "bad",
            result:
              "⚠️ (Simulated) A cloud breach exposes your phrase and your funds vanish. Screenshots are a common way people get robbed.",
          },
          {
            id: "c",
            label: "Just memorize it, no backup needed",
            correct: false,
            outcome: "bad",
            result:
              "⚠️ Human memory is unreliable. Forget one word or its order, and the money is gone forever.",
          },
        ],
        why: "Self-custody trades convenience for control. The trade-off is real: no one can freeze your funds, but no one can recover them for you either. A safe offline backup is what makes self-custody actually safe.",
      },
    ],
  },
  {
    id: "send-receive",
    title: "Send & Receive BCH",
    tagline: "Move money, understand fees & confirmations.",
    emoji: "💸",
    gradient: "from-lime-500 to-green-600",
    image: "/modules/send-receive.jpg",
    lessons: [
      {
        id: "send-1",
        moduleId: "send-receive",
        title: "Receiving BCH",
        summary: "Share the right address.",
        xp: 20,
        learn: [
          "To receive BCH, you share your address — either as text or as a QR code that the sender scans. The sender types in an amount and hits send.",
          "Addresses are long strings of letters and numbers, so QR codes exist to prevent typos. Always double-check the first and last few characters.",
          "You can reuse an address, but many wallets give you a fresh one each time for extra privacy.",
        ],
        factBox: [
          { label: "You share", value: "Your address / QR code" },
          { label: "Simulated balance before", value: "0.00 BCH" },
          { label: "Incoming (simulated)", value: "+0.05 BCH" },
        ],
        scenario:
          "A customer wants to pay you 0.05 BCH in person. What's the smoothest way to receive it?",
        choices: [
          {
            id: "a",
            label: "Show your QR code and let them scan it",
            correct: true,
            outcome: "good",
            result:
              "✅ They scan, confirm, and send. Simulated balance: 0.00 → 0.05 BCH. No typos, no fuss.",
          },
          {
            id: "b",
            label: "Read your 42-character address aloud for them to type",
            correct: false,
            outcome: "bad",
            result:
              "⚠️ One wrong character and the payment could go to the wrong place — and BCH payments can't be reversed. QR codes exist for exactly this reason.",
          },
        ],
        why: "Receiving is the easy half: you just share an address. QR codes make it error-proof. And remember — because payments are final, getting the address right matters more than with a bank transfer you could reverse.",
      },
      {
        id: "send-2",
        moduleId: "send-receive",
        title: "Sending & fees",
        summary: "Why fees are so small.",
        xp: 25,
        learn: [
          "When you send BCH, you pay a small network fee to the people running the network (miners) who confirm your payment. On BCH this fee is typically a tiny fraction of a cent.",
          "The fee is roughly based on the SIZE of your transaction in data, NOT the amount of money. Sending $5 or $5,000 costs about the same tiny fee.",
          "This is very different from credit cards or wire transfers, which often charge a percentage of the amount.",
        ],
        factBox: [
          { label: "Send amount (simulated)", value: "$500 in BCH" },
          { label: "Network fee (simulated)", value: "~$0.002" },
          { label: "Credit card equivalent", value: "~$15 (3%)" },
        ],
        scenario:
          "You need to send $500 to family overseas. A money-transfer service charges $25. What does BCH cost you (simulated)?",
        choices: [
          {
            id: "a",
            label: "About a fraction of a cent, regardless of the amount",
            correct: true,
            outcome: "good",
            result:
              "✅ Your family receives essentially the full $500. The simulated fee is a rounding error compared to the $25 service.",
          },
          {
            id: "b",
            label: "About 3% of $500, so ~$15",
            correct: false,
            outcome: "neutral",
            result:
              "ℹ️ That's how cards and many services price fees — as a percentage. BCH doesn't work that way; its fee is based on data size, not dollar amount.",
          },
        ],
        why: "BCH fees are based on transaction size in bytes, not the value being sent. That's why sending a fortune costs the same tiny amount as sending a few dollars — a huge deal for remittances and large payments.",
      },
      {
        id: "send-3",
        moduleId: "send-receive",
        title: "Confirmations",
        summary: "When is a payment 'done'?",
        xp: 25,
        learn: [
          "After you send BCH, the network records it. A 'confirmation' means the network has permanently included your payment in its shared ledger.",
          "For everyday amounts — a coffee, a meal, a tip — a payment is safe to accept the instant it appears (this is often called '0-conf'). It shows up in seconds.",
          "For large or high-value payments, waiting for one or more confirmations (usually a few minutes) adds extra certainty that it can't be undone.",
        ],
        factBox: [
          { label: "Appears in wallet", value: "Seconds (simulated)" },
          { label: "1 confirmation", value: "~10 minutes avg (simulated)" },
          { label: "Coffee purchase", value: "Accept instantly" },
        ],
        scenario:
          "You're selling a $4 coffee and the customer's BCH payment just popped up on your screen. Do you hand over the coffee?",
        choices: [
          {
            id: "a",
            label: "Yes — for a small amount it's fine to accept instantly",
            correct: true,
            outcome: "good",
            result:
              "✅ Coffee handed over, customer happy, line keeps moving. For low-value items, instant acceptance is the norm.",
          },
          {
            id: "b",
            label: "No — make them wait 10 minutes for a confirmation",
            correct: false,
            outcome: "bad",
            result:
              "😬 The line backs up and the customer is annoyed. Waiting for confirmations on a $4 coffee is overkill.",
          },
        ],
        why: "Confirmations are about certainty vs. speed. A tiny payment carries tiny risk, so accepting instantly makes sense. A car purchase? Wait a few minutes for a confirmation. Match the caution to the value.",
      },
    ],
  },
  {
    id: "payments",
    title: "BCH Payments",
    tagline: "Everyday spending scenarios.",
    emoji: "🛒",
    gradient: "from-emerald-500 to-teal-600",
    image: "/modules/payments.jpg",
    lessons: [
      {
        id: "payments-1",
        moduleId: "payments",
        title: "Paying at a shop",
        summary: "Scan, confirm, done.",
        xp: 20,
        learn: [
          "Paying with BCH in person is usually just: the shop shows a QR code with the amount, you scan it with your wallet, review, and tap send.",
          "The whole thing takes a few seconds. No card, no PIN pad, no signature — just a scan and a confirmation.",
          "Because you approve the exact amount shown, there's no risk of being overcharged or of a card number being stored somewhere.",
        ],
        scenario:
          "You're buying groceries for $32.50. The cashier shows a QR code. What's the flow?",
        choices: [
          {
            id: "a",
            label: "Scan the QR, check it says $32.50, tap send",
            correct: true,
            outcome: "good",
            result:
              "✅ Paid in ~5 seconds (simulated). You saw and approved the exact amount before sending.",
          },
          {
            id: "b",
            label: "Type the shop's address and guess the amount",
            correct: false,
            outcome: "bad",
            result:
              "⚠️ Slow and error-prone. The QR encodes the exact amount for you — always prefer scanning.",
          },
        ],
        why: "In-person BCH payments are designed to be as fast as tapping a card, but with you approving the exact amount. The QR carries the address AND the amount, so there's nothing to guess.",
      },
      {
        id: "payments-2",
        moduleId: "payments",
        title: "Price volatility",
        summary: "BCH price can move.",
        xp: 25,
        learn: [
          "BCH's price in dollars can go up and down. If you hold BCH and prices drop, the dollar value of your holdings drops too.",
          "For spending, many people keep only what they need in BCH and think in their local currency. Wallets show you the live dollar value so you always know what you're spending.",
          "Merchants who don't want price swings can instantly convert received BCH to their local currency — you'll explore this in the Merchant module.",
        ],
        factBox: [
          { label: "Monday (simulated)", value: "1 BCH = $500" },
          { label: "Friday (simulated)", value: "1 BCH = $460" },
          { label: "Lesson", value: "Only hold what you're comfortable with" },
        ],
        scenario:
          "You got paid 1 BCH (simulated $500) and you'll need that $500 for rent next week. What's the sensible move?",
        choices: [
          {
            id: "a",
            label: "Convert the rent money to dollars/stablecoin now",
            correct: true,
            outcome: "good",
            result:
              "✅ Your rent money stays worth $500 no matter what BCH's price does. You removed the risk you didn't want.",
          },
          {
            id: "b",
            label: "Hold it all as BCH and hope the price rises",
            correct: false,
            outcome: "bad",
            result:
              "⚠️ (Simulated) By Friday BCH dropped to $460 — you're $40 short on rent. Money you NEED soon shouldn't ride the market.",
          },
        ],
        why: "BCH is great for moving money, but its price fluctuates. A simple rule: don't gamble money you need for bills. Convert obligations to stable value, and only keep in BCH what you can afford to let float.",
      },
    ],
  },
  {
    id: "merchant",
    title: "Become a BCH Merchant",
    tagline: "Run a simulated business.",
    emoji: "🏪",
    gradient: "from-green-600 to-lime-500",
    image: "/modules/merchant.jpg",
    lessons: [
      {
        id: "merchant-1",
        moduleId: "merchant",
        title: "Accepting your first payment",
        summary: "Set up a point of sale.",
        xp: 25,
        learn: [
          "To accept BCH at your shop, you use a free point-of-sale app or web page. You type in the price, it shows a QR code, and the customer scans it.",
          "Unlike card terminals, there are no monthly hardware rentals and no percentage cut taken from every sale. You keep essentially the whole payment.",
          "Payments land directly in YOUR wallet — no processor holding your money for days.",
        ],
        factBox: [
          { label: "Card processor fee", value: "~2.9% + $0.30" },
          { label: "BCH network fee (simulated)", value: "< $0.01" },
          { label: "Payout time", value: "Instant to your wallet" },
        ],
        scenario:
          "You run a food stall. A $9 order comes in. With cards you'd lose ~$0.56 in fees. With BCH (simulated)?",
        choices: [
          {
            id: "a",
            label: "Keep almost the full $9 — fee is under a cent",
            correct: true,
            outcome: "good",
            result:
              "✅ Over a busy day of 200 orders, you save ~$110 in fees compared to cards (simulated). That's real margin.",
          },
          {
            id: "b",
            label: "Lose the same ~2.9% as cards",
            correct: false,
            outcome: "neutral",
            result:
              "ℹ️ Nope — BCH fees aren't a percentage. Your cost per sale stays a fraction of a cent no matter the order size.",
          },
        ],
        why: "For merchants, fees add up fast. Card processors take a percentage of every single sale plus a fixed fee. BCH's flat tiny fee means thin-margin businesses keep more of what they earn.",
      },
      {
        id: "merchant-2",
        moduleId: "merchant",
        title: "Managing price risk",
        summary: "Hold BCH or auto-convert?",
        xp: 25,
        learn: [
          "As a merchant, once you receive BCH you have a choice: keep it as BCH, or automatically convert it to your local currency the moment it arrives.",
          "Auto-converting protects your margins from price swings — a $9 sale stays worth $9. Holding BCH means you're also betting on the price, which is a separate decision from running your business.",
          "Many merchants auto-convert their costs (rent, supplies) and keep only a portion in BCH if they choose to.",
        ],
        scenario:
          "Your stall has thin margins and you need predictable income to pay suppliers. What's the safer default?",
        choices: [
          {
            id: "a",
            label: "Auto-convert sales to local currency instantly",
            correct: true,
            outcome: "good",
            result:
              "✅ Your books are predictable: every $9 sale is $9. You get BCH's low fees AND stable revenue.",
          },
          {
            id: "b",
            label: "Hold every sale as BCH and watch the market",
            correct: false,
            outcome: "bad",
            result:
              "⚠️ (Simulated) A rough market week shrinks your revenue by 8% — now you can't fully pay a supplier. Business risk and market risk got tangled together.",
          },
        ],
        why: "Running a business and speculating on price are two different jobs. Auto-conversion lets you enjoy BCH's speed and low fees while keeping revenue stable. You can always choose to hold some BCH — but that should be a deliberate choice, not an accident.",
      },
    ],
  },
  {
    id: "bch-vs-btc",
    title: "BCH vs BTC",
    tagline: "Side-by-side scenarios.",
    emoji: "⚖️",
    gradient: "from-yellow-500 to-green-600",
    image: "/modules/bch-vs-btc.jpg",
    lessons: [
      {
        id: "vs-1",
        moduleId: "bch-vs-btc",
        title: "Same roots, different goals",
        summary: "Store of value vs. cash.",
        xp: 20,
        learn: [
          "BCH and BTC share the same origins — they split apart in 2017. Since then they've pursued different goals.",
          "BTC (Bitcoin) is often described as 'digital gold' — a store of value where keeping blocks small is prioritized, which can mean higher fees when it's busy.",
          "BCH (Bitcoin Cash) prioritizes being usable 'digital cash' — larger blocks and low fees so everyday payments stay cheap.",
          "Neither is 'wrong' — they optimize for different uses. This module helps you pick the right tool for a given job.",
        ],
        factBox: [
          { label: "BTC nickname", value: "Digital gold" },
          { label: "BCH nickname", value: "Digital cash" },
          { label: "Split", value: "2017" },
        ],
        scenario:
          "You want to buy a $3 sandwich on-chain. Which design is a better fit for that job?",
        choices: [
          {
            id: "a",
            label: "The low-fee, cash-optimized one (BCH)",
            correct: true,
            outcome: "good",
            result:
              "✅ A sub-cent fee on a $3 sandwich makes sense. This is exactly what 'digital cash' is optimized for.",
          },
          {
            id: "b",
            label: "The store-of-value one where fees can spike (BTC on-chain)",
            correct: false,
            outcome: "neutral",
            result:
              "ℹ️ On a busy day, the on-chain fee could rival the sandwich price. Great as digital gold, awkward for a $3 lunch.",
          },
        ],
        why: "This isn't about one being better overall — it's about fit. For small, frequent, everyday payments, BCH's low-fee design shines. For long-term holding, people often prefer BTC. Choose the tool that matches the task.",
      },
      {
        id: "vs-2",
        moduleId: "bch-vs-btc",
        title: "Fees under load",
        summary: "What happens when it's busy?",
        xp: 25,
        learn: [
          "When lots of people transact at once, block space becomes a hot commodity. Networks with limited block space see fees rise as users bid to get included.",
          "BCH keeps more block space available, so fees tend to stay low even when the network is busy. BTC's smaller blocks can lead to fee spikes during peak demand.",
          "For a payments-first experience, predictable low fees matter more than almost anything else.",
        ],
        factBox: [
          { label: "Quiet day", value: "Both cheap (simulated)" },
          { label: "Busy day — BCH", value: "Still < 1¢ (simulated)" },
          { label: "Busy day — BTC on-chain", value: "Can spike to $$ (simulated)" },
        ],
        scenario:
          "It's a super busy day on both networks. You need to send a $20 payment on-chain and want a predictable, tiny fee.",
        choices: [
          {
            id: "a",
            label: "Use BCH — big blocks keep fees low even under load",
            correct: true,
            outcome: "good",
            result:
              "✅ (Simulated) Fee stays under a cent. Your $20 arrives cheaply despite the traffic.",
          },
          {
            id: "b",
            label: "Send it on BTC on-chain and hope fees are low today",
            correct: false,
            outcome: "bad",
            result:
              "⚠️ (Simulated) Peak demand pushes the fee up several dollars — a noticeable chunk of a $20 payment.",
          },
        ],
        why: "Block space is like road capacity. When demand surges on a narrow road, tolls spike. BCH keeps the road wide so tolls stay tiny. That predictability is why BCH leans into the 'everyday payments' role.",
      },
    ],
  },
  {
    id: "safety",
    title: "Staying Safe",
    tagline: "Spot scams and protect your money.",
    emoji: "🛡️",
    gradient: "from-rose-500 to-red-600",
    image: "/modules/safety.jpg",
    lessons: [
      {
        id: "safety-1",
        moduleId: "safety",
        title: "Spotting common scams",
        summary: "If it sounds too good to be true…",
        xp: 25,
        learn: [
          "Because BCH payments are final and you're your own bank, scammers can't rely on a bank to reverse things — so they target YOU directly. The good news: almost every scam uses the same few tricks.",
          "The classics: 'send 1 BCH and get 2 back' giveaways, fake support staff asking for your recovery phrase, 'you've won a prize, just pay a small fee', and urgent messages pressuring you to act right now.",
          "The golden rule: nobody gives away free money, real support never needs your secret words, and urgency is a manipulation tactic. Slow down and the scam usually falls apart.",
        ],
        factBox: [
          { label: "Classic bait", value: "'Double your BCH!'" },
          { label: "Pressure tactic", value: "'Act now or lose it'" },
          { label: "Your defense", value: "Slow down & verify" },
        ],
        scenario:
          "A popular account posts: 'Sending BCH to the first 100 people! Send 0.1 BCH to verify your address and receive 0.5 BCH back instantly.' What do you do?",
        choices: [
          {
            id: "a",
            label: "Ignore it — no one gives away free money for free",
            correct: true,
            outcome: "good",
            result:
              "✅ Exactly right. This is the oldest crypto scam there is. The moment you 'verify' by sending, your BCH is gone for good.",
          },
          {
            id: "b",
            label: "Send 0.1 BCH quickly before the spots run out",
            correct: false,
            outcome: "bad",
            result:
              "❌ (Simulated) Your 0.1 BCH vanishes and nothing comes back. 'Send to receive' is always a scam — there's no exception.",
          },
        ],
        why: "Scammers exploit greed and urgency. A real giveaway never requires you to send money first, and legitimate projects never DM you asking for funds. If an offer only works if you act fast and don't think — that's the scam talking.",
      },
      {
        id: "safety-2",
        moduleId: "safety",
        title: "Verify before you trust",
        summary: "Check addresses and links.",
        xp: 25,
        learn: [
          "Two habits stop most losses: verifying addresses and verifying links. Malware can silently swap a copied address for the attacker's, so always eyeball the first and last few characters after pasting.",
          "Fake websites and apps are everywhere. A 'wallet' downloaded from a random link or a sponsored search ad can be a trap designed to steal your recovery phrase.",
          "Only download wallets from official sources, bookmark the real sites you use, and when in doubt, type the address in yourself rather than clicking a link.",
        ],
        factBox: [
          { label: "Check", value: "First & last address chars" },
          { label: "Downloads", value: "Official sources only" },
          { label: "Links", value: "Bookmark, don't search" },
        ],
        scenario:
          "You copy a friend's BCH address to send them $20. After pasting, the address starts with a different set of characters than you remember. What now?",
        choices: [
          {
            id: "a",
            label: "Stop and re-check — malware may have swapped the address",
            correct: true,
            outcome: "good",
            result:
              "✅ Great instinct. 'Clipboard hijacking' malware swaps addresses at the last second. Verifying the characters just saved your $20.",
          },
          {
            id: "b",
            label: "Send anyway — addresses always look random",
            correct: false,
            outcome: "bad",
            result:
              "⚠️ (Simulated) The payment lands in an attacker's wallet and can't be reversed. The few seconds to verify are always worth it.",
          },
        ],
        why: "Verification is cheap; mistakes are permanent. Checking a handful of characters and sticking to official sources costs you seconds but defeats the most common ways people lose funds. Make it a reflex.",
      },
      {
        id: "safety-3",
        moduleId: "safety",
        title: "Recovering from a mistake",
        summary: "What can (and can't) be undone.",
        xp: 20,
        learn: [
          "Self-custody means power and responsibility. Some mistakes are recoverable, and some aren't — knowing the difference helps you stay calm and act correctly.",
          "Recoverable: lost or broken phone (restore from your recovery phrase). NOT recoverable: sending to the wrong address, losing your recovery phrase with no backup, or sharing your phrase with a scammer.",
          "This is why backups and double-checking matter so much. The network has no 'customer support' to call — your habits ARE your safety net.",
        ],
        factBox: [
          { label: "Lost phone", value: "Recoverable (with phrase)" },
          { label: "Wrong address", value: "Not recoverable" },
          { label: "Lost phrase", value: "Not recoverable" },
        ],
        scenario:
          "Your phone falls in a lake. You had a self-custody BCH wallet on it with a proper written backup. How worried should you be about your funds?",
        choices: [
          {
            id: "a",
            label: "Not worried — restore the wallet on a new phone with my 12 words",
            correct: true,
            outcome: "good",
            result:
              "✅ Calm and correct. Your coins live on the network, not the phone. Type your recovery phrase into a new wallet and everything reappears.",
          },
          {
            id: "b",
            label: "Panic — the money is gone with the phone",
            correct: false,
            outcome: "neutral",
            result:
              "ℹ️ Understandable fear, but no! The phone only held keys. Because you backed up your phrase, the funds are perfectly safe.",
          },
        ],
        why: "Your recovery phrase is the ultimate backup — it makes hardware failures a non-event. But it only works if you actually wrote it down safely. Good habits turn scary moments into minor inconveniences.",
      },
    ],
  },
  {
    id: "cashtokens",
    title: "CashTokens & More",
    tagline: "Tokens and smart features on BCH.",
    emoji: "🎨",
    gradient: "from-violet-500 to-purple-600",
    image: "/modules/cashtokens.jpg",
    lessons: [
      {
        id: "tokens-1",
        moduleId: "cashtokens",
        title: "What are CashTokens?",
        summary: "More than just money.",
        xp: 20,
        learn: [
          "In 2023, BCH gained 'CashTokens' — a way to create custom tokens and digital collectibles (NFTs) directly on the BCH network, using the same cheap, fast transactions you already know.",
          "Fungible tokens are interchangeable, like points, shares, or a stablecoin. Non-fungible tokens (NFTs) are unique — think a ticket, a certificate, or a collectible.",
          "Because they ride on BCH, moving a token costs the same tiny fee as sending BCH — no expensive 'gas' fees like on some other networks.",
        ],
        factBox: [
          { label: "Launched", value: "May 2023" },
          { label: "Fungible", value: "Points, stablecoins, shares" },
          { label: "Non-fungible", value: "Tickets, collectibles" },
        ],
        scenario:
          "A concert wants to issue tickets that can't be counterfeited and can be resold safely. Which BCH feature fits?",
        choices: [
          {
            id: "a",
            label: "Non-fungible CashTokens — each ticket is unique and verifiable",
            correct: true,
            outcome: "good",
            result:
              "✅ Each ticket becomes a unique token the venue can verify at the door, and fans can transfer it safely for a fraction of a cent.",
          },
          {
            id: "b",
            label: "Nothing — BCH can only send money",
            correct: false,
            outcome: "bad",
            result:
              "ℹ️ Not since 2023! CashTokens let BCH handle unique digital items like tickets, not just payments.",
          },
        ],
        why: "CashTokens extend BCH beyond simple payments into tickets, loyalty points, stablecoins, and collectibles — all with BCH's signature low fees. It turns 'digital cash' into a broader toolkit while keeping the same simple foundation.",
      },
      {
        id: "tokens-2",
        moduleId: "cashtokens",
        title: "Stablecoins on BCH",
        summary: "Digital dollars, BCH speed.",
        xp: 25,
        learn: [
          "A stablecoin is a token designed to stay worth about $1 (or another currency). On BCH, stablecoin-style tokens let you hold steady value while keeping BCH's instant, near-free transfers.",
          "This solves the volatility problem for people who want the speed of BCH but not the price swings — useful for savings, payroll, or merchants who want predictable value.",
          "You can move between BCH and stable tokens as your needs change: spend or save in something steady, or hold BCH when you want to.",
        ],
        factBox: [
          { label: "Goal", value: "Stay ~$1 in value" },
          { label: "Benefit", value: "BCH speed, stable value" },
          { label: "Great for", value: "Savings & payroll" },
        ],
        scenario:
          "You're paid in BCH but want to set aside next month's rent so its value can't drop. What's a sensible BCH-native option?",
        choices: [
          {
            id: "a",
            label: "Convert the rent portion into a stablecoin token on BCH",
            correct: true,
            outcome: "good",
            result:
              "✅ Your rent money stays steady in value, but still moves with BCH-level speed and tiny fees. Best of both worlds.",
          },
          {
            id: "b",
            label: "Leave it all in BCH and hope the price holds",
            correct: false,
            outcome: "bad",
            result:
              "⚠️ (Simulated) A price dip leaves you short on rent — the exact risk stablecoins are meant to remove for money you'll need soon.",
          },
        ],
        why: "Stablecoins give you a place to park value that shouldn't fluctuate, without leaving the BCH ecosystem. Keep obligations stable, hold BCH when you want price exposure, and enjoy fast cheap transfers either way.",
      },
    ],
  },
  {
    id: "real-world",
    title: "BCH in the Real World",
    tagline: "From lessons to actually using it.",
    emoji: "🌍",
    gradient: "from-sky-500 to-blue-600",
    image: "/modules/real-world.jpg",
    lessons: [
      {
        id: "real-1",
        moduleId: "real-world",
        title: "Your first real wallet",
        summary: "Take the leap safely.",
        xp: 20,
        learn: [
          "Everything so far has been simulated. Making your first REAL wallet is simple: download a reputable self-custody wallet, let it generate a recovery phrase, and write those words down on paper.",
          "Start tiny. Receive a small amount — even a dollar's worth — so your very first real transaction is low-stakes while you get comfortable.",
          "Practice the full loop with small money: receive, check the balance, send a little to a friend, and restore the wallet on another device to prove your backup works.",
        ],
        factBox: [
          { label: "Step 1", value: "Download a real wallet" },
          { label: "Step 2", value: "Write down your 12 words" },
          { label: "Step 3", value: "Start with a tiny amount" },
        ],
        scenario:
          "You just installed a real BCH wallet. Before receiving your first coins, what's the most important thing to do?",
        choices: [
          {
            id: "a",
            label: "Write down the recovery phrase on paper and store it safely",
            correct: true,
            outcome: "good",
            result:
              "✅ Perfect. With the backup safely written down, you're ready to receive real BCH knowing you can always recover it.",
          },
          {
            id: "b",
            label: "Skip the backup — I'll do it later once I have more coins",
            correct: false,
            outcome: "bad",
            result:
              "⚠️ 'Later' is how people lose everything. Back up FIRST, before any real money touches the wallet.",
          },
        ],
        why: "The recovery phrase is the one thing you can't recreate. Backing up before you fund the wallet means you're never in a position to lose real money. Start small, prove your backup, then scale up with confidence.",
      },
      {
        id: "real-2",
        moduleId: "real-world",
        title: "Spending & getting paid",
        summary: "Find places that accept BCH.",
        xp: 20,
        learn: [
          "BCH is used all over the world — from street markets in the Philippines to online stores and freelancers accepting it for work. Directories and maps help you find merchants near you.",
          "The easiest way to start using BCH for real is to get some, then spend a little: buy a gift card, tip a creator, or pay a friend back. Real usage builds real confidence.",
          "Getting paid in BCH is just as easy — share your address or QR code, and money lands directly in your wallet with no processor in between.",
        ],
        factBox: [
          { label: "Find merchants", value: "BCH maps & directories" },
          { label: "Easy first spend", value: "Gift cards, tips" },
          { label: "Get paid", value: "Share your QR code" },
        ],
        scenario:
          "You want to actually USE your BCH this week for the first time. What's a great low-pressure way to start?",
        choices: [
          {
            id: "a",
            label: "Tip a creator or buy a small gift card with BCH",
            correct: true,
            outcome: "good",
            result:
              "✅ A small, real transaction turns theory into experience. You'll feel how fast and cheap it is — and it's genuinely fun.",
          },
          {
            id: "b",
            label: "Wait until you fully understand every technical detail",
            correct: false,
            outcome: "neutral",
            result:
              "ℹ️ You don't need to be an expert to start! A tiny real payment teaches you more than weeks of reading. Just keep it small at first.",
          },
        ],
        why: "BCH only clicks once you use it. A small real transaction — a tip, a gift card, paying a friend — cements everything you've learned and connects you to a global community already using peer-to-peer cash every day.",
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// Final quiz — a knowledge check the learner must pass to earn the certificate.
// Questions draw on the whole curriculum. All content is educational/simulated.
// ---------------------------------------------------------------------------

export interface QuizOption {
  id: string;
  label: string;
}

export interface QuizQuestion {
  id: string;
  question: string;
  options: QuizOption[];
  /** id of the correct option */
  answer: string;
  /** Short explanation shown after answering. */
  explanation: string;
}

export const QUIZ: QuizQuestion[] = [
  {
    id: "q1",
    question: "What does 'peer-to-peer electronic cash' mean for Bitcoin Cash?",
    options: [
      { id: "a", label: "A bank moves the money between people for a fee" },
      { id: "b", label: "You can send money directly to anyone, with no middleman" },
      { id: "c", label: "Only businesses can send and receive it" },
    ],
    answer: "b",
    explanation:
      "BCH lets value move directly between people over an open network — no bank or company in the middle.",
  },
  {
    id: "q11",
    question:
      "You keep $1,000 in cash and prices rise 5% over the year. What happened to your money?",
    options: [
      { id: "a", label: "It still says $1,000 but buys about $50 less" },
      { id: "b", label: "It grew to $1,050 automatically" },
      { id: "c", label: "Nothing — cash never loses value" },
    ],
    answer: "a",
    explanation:
      "Inflation is a silent tax on savers: the number stays the same, but each dollar buys less as prices rise.",
  },
  {
    id: "q12",
    question: "Why does a fixed, capped money supply help protect value?",
    options: [
      { id: "a", label: "Because no one can print more to dilute what you hold" },
      { id: "b", label: "Because it makes prices rise faster" },
      { id: "c", label: "Because banks can expand it when needed" },
    ],
    answer: "a",
    explanation:
      "Sound money like BCH has a hard 21-million cap no one can change, so your share can't be quietly inflated away.",
  },
  {
    id: "q2",
    question: "Someone online offers to help and asks for your 12-word recovery phrase. You should:",
    options: [
      { id: "a", label: "Share it — they seem like they know what they're doing" },
      { id: "b", label: "Never share it; no legitimate person or app needs it" },
      { id: "c", label: "Share only the first six words to be safe" },
    ],
    answer: "b",
    explanation:
      "Your recovery phrase IS your money. Anyone who has it controls your funds. Never share it — not even partially.",
  },
  {
    id: "q3",
    question: "Roughly what determines the network fee when you send BCH?",
    options: [
      { id: "a", label: "A percentage of the amount you send" },
      { id: "b", label: "The data size of the transaction, not the dollar amount" },
      { id: "c", label: "The time of day you send it" },
    ],
    answer: "b",
    explanation:
      "BCH fees are based on transaction size in bytes, so sending $5 or $5,000 costs about the same tiny fee.",
  },
  {
    id: "q4",
    question: "A customer pays for a $4 coffee with BCH and the payment appears on your screen. You should:",
    options: [
      { id: "a", label: "Hand over the coffee — instant acceptance is fine for small amounts" },
      { id: "b", label: "Make them wait 10 minutes for a confirmation" },
      { id: "c", label: "Refuse BCH for anything under $10" },
    ],
    answer: "a",
    explanation:
      "For small everyday amounts, accepting instantly ('0-conf') is normal. Save confirmation waits for high-value payments.",
  },
  {
    id: "q5",
    question: "You'll need $500 for rent next week and you just got paid in BCH. The safest move is to:",
    options: [
      { id: "a", label: "Hold it all as BCH and hope the price goes up" },
      { id: "b", label: "Convert the rent money to stable value now" },
      { id: "c", label: "Send it to a friend for safekeeping" },
    ],
    answer: "b",
    explanation:
      "Don't gamble money you need soon. Convert obligations to stable value; only keep in BCH what you can let fluctuate.",
  },
  {
    id: "q6",
    question: "Which is a classic scam you should always ignore?",
    options: [
      { id: "a", label: "'Send 0.1 BCH to verify and receive 0.5 BCH back instantly'" },
      { id: "b", label: "A friend sending you their QR code to receive a payment" },
      { id: "c", label: "A merchant showing a QR code with the exact price" },
    ],
    answer: "a",
    explanation:
      "'Send to receive' giveaways are always scams. Nobody gives away free money, and payments can't be reversed.",
  },
  {
    id: "q7",
    question: "How is BCH's design goal different from BTC's?",
    options: [
      { id: "a", label: "BCH aims to be everyday 'digital cash'; BTC leans toward 'digital gold'" },
      { id: "b", label: "They are identical in every way" },
      { id: "c", label: "BCH can't be used for payments at all" },
    ],
    answer: "a",
    explanation:
      "BCH prioritizes cheap, fast everyday payments, while BTC is often used as a store of value.",
  },
  {
    id: "q8",
    question: "What did CashTokens add to Bitcoin Cash in 2023?",
    options: [
      { id: "a", label: "The ability to create custom tokens and NFTs on BCH" },
      { id: "b", label: "A way to reverse transactions" },
      { id: "c", label: "Higher fees for all payments" },
    ],
    answer: "a",
    explanation:
      "CashTokens let BCH handle fungible tokens (like stablecoins) and unique NFTs (like tickets) with the same low fees.",
  },
  {
    id: "q9",
    question: "Your phone breaks, but you wrote down your recovery phrase. Your BCH is:",
    options: [
      { id: "a", label: "Gone forever with the phone" },
      { id: "b", label: "Safe — restore it on a new device with your 12 words" },
      { id: "c", label: "Only recoverable by contacting BCH support" },
    ],
    answer: "b",
    explanation:
      "Your coins live on the network, not the phone. A written backup makes hardware failure a non-event.",
  },
  {
    id: "q10",
    question: "Before funding your first REAL wallet, the most important step is to:",
    options: [
      { id: "a", label: "Write down and safely store your recovery phrase" },
      { id: "b", label: "Deposit a large amount to make it worthwhile" },
      { id: "c", label: "Screenshot your phrase and save it to the cloud" },
    ],
    answer: "a",
    explanation:
      "Back up your phrase safely BEFORE any real money touches the wallet. Never store it as a cloud screenshot.",
  },
];

/** Percentage of correct answers required to pass the final quiz. */
export const QUIZ_PASS_THRESHOLD = 0.7;
export const QUIZ_PASS_COUNT = Math.ceil(QUIZ.length * QUIZ_PASS_THRESHOLD);

export const ALL_LESSONS: Lesson[] = MODULES.flatMap((m) => m.lessons);
export const TOTAL_XP = ALL_LESSONS.reduce((sum, l) => sum + l.xp, 0);
export const TOTAL_LESSONS = ALL_LESSONS.length;

export function getModule(id: string): Module | undefined {
  return MODULES.find((m) => m.id === id);
}

export function getLesson(id: string): Lesson | undefined {
  return ALL_LESSONS.find((l) => l.id === id);
}

